import { useState } from 'react'
import { NavRightClickMenuProps } from './Types'
import { LocationPicker, SelectedFolder } from 'components/LocationPicker/LocationPicker'
import { useEvaluateMove } from 'components/LocationPicker/hooks/useEvaluateMove'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { getAllDocsPath, getFolderPath } from 'utils/urlUtils'
import { showSuccessfulMoveToast } from 'common/ToastMessage/showMoveToast'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { moveError } from 'components/NavRightClickMenu/components/MoveError/MoveError'
import { NavRightClickMenu } from './NavRightClickMenu'
import { FolderClient } from 'api/clients/FolderClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from './NavRightClickMenu.module.scss'
import { BlazeAI } from 'almanac-ui/icons/16'
import { showSuccessfulCopyToast } from 'common/ToastMessage/showCopyToast'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { message } from 'antd'
import { pluralize } from 'utils'

export const NavRightClickMenuContainer = observer(
  ({
    selectedFiles,
    onAction,
    initialFolder,
    onMoveRequest,
    setVisible,
    onCopyRequest,
    ...rest
  }: NavRightClickMenuProps) => {
    const [showMove, setShowMove] = useState(false)
    const [showCopy, setShowCopy] = useState(false)
    const workspaceId = useCurrentWorkspaceId()
    const { evaluateMove } = useEvaluateMove()
    const rootStore = useRootStore()

    const copyFiles = async ({
      selectedFolder,
      newWorkspaceId,
    }: {
      selectedFolder: { title: string; id: string; handbook?: { seo_string: string } } | null
      newWorkspaceId: number
    }) => {
      const numFiles = selectedFiles.length
      const selectedFolderId = !!selectedFolder ? selectedFolder.id : null
      const folderIds: string[] = []
      const articleIds: string[] = []
      const fileIds: string[] = []
      selectedFiles.forEach((file) => {
        if (file.type === 'folder') folderIds.push(file.id)
        if (file.type === 'article' || file.type === 'handbook') articleIds.push(file.id)
        if (file.type === 'design' || file.type === 'media_file') fileIds.push(file.id)
      })

      setShowCopy(false)
      const hideLoading = message.loading(
        <div className={Styles.loadingMessage}>
          <BlazeAI /> Copying {numFiles} {pluralize('item', numFiles)}...
        </div>,
        0,
      )

      const response = await FolderClient.bulkCopyFiles({
        folderIds: folderIds,
        articleIds: articleIds,
        fileIds,
        destinationFolderId: selectedFolderId,
        destinationWorkspaceId: newWorkspaceId,
      })

      if (response.type === SUCCESS_RESPONSE_TYPE) {
        hideLoading()
        const url = selectedFolder?.handbook?.seo_string
          ? getFolderPath({ folderSlug: selectedFolder.handbook.seo_string })
          : getAllDocsPath(newWorkspaceId)
        onCopyRequest(selectedFiles, selectedFolderId, newWorkspaceId)
        showSuccessfulCopyToast(numFiles, url, selectedFolder?.title)

        return
      }
      hideLoading()
      showErrorMessageToast('Something went wrong')
    }

    const moveFiles = async ({
      selectedFolder,
      newWorkspaceId,
    }: {
      selectedFolder: SelectedFolder | null
      newWorkspaceId: number
    }) => {
      const numFiles = selectedFiles.length
      const selectedFolderId = !!selectedFolder ? selectedFolder.id : null
      const folderIds: string[] = []
      const articleIds: string[] = []
      const fileIds: string[] = []
      selectedFiles.forEach((file) => {
        if (file.type === 'folder') folderIds.push(file.id)
        if (file.type === 'article' || file.type === 'handbook') articleIds.push(file.id)
        if (file.type === 'media_file' || file.type === 'design') fileIds.push(file.id)
      })

      const dismissLoadingToast = showToastMessage('Moving...')
      const response = await FolderClient.bulkMoveFiles({
        folderIds: folderIds,
        articleIds: articleIds,
        fileIds,
        destinationFolderId: selectedFolderId,
        destinationWorkspaceId: newWorkspaceId,
        originalWorkspaceId: workspaceId,
      })
      dismissLoadingToast()

      if (response.type === SUCCESS_RESPONSE_TYPE) {
        const url = selectedFolder?.seo_string
          ? getFolderPath({ folderSlug: selectedFolder.seo_string })
          : getAllDocsPath(newWorkspaceId)
        setShowMove(false)
        onMoveRequest(selectedFiles, selectedFolderId, newWorkspaceId)
        showSuccessfulMoveToast(numFiles, url, selectedFolder?.title)

        rootStore.handbooks.refresh(initialFolder?.id)
        rootStore.handbooks.refresh(selectedFolder?.handbook_key)
        return
      }
      if (response.type === 'error') {
        moveError({ status: response.status, data: response.data })
      }
      setShowMove(false)
    }

    if (showMove || showCopy) {
      return (
        <LocationPicker
          initialFolder={
            initialFolder
              ? {
                  id: initialFolder.id,
                  title: initialFolder.title,
                  ancestor_keys: initialFolder.ancestor_projects.map((p) => p.id),
                }
              : null
          }
          actionType={showMove ? 'move' : 'copy'}
          onSelectFolder={(data) => {
            if (showMove) {
              if (!workspaceId) return

              const folderIds: string[] = []
              const articleIds: string[] = []

              selectedFiles.forEach((file) => {
                if (file.type === 'folder') folderIds.push(file.id)
                if (file.type === 'article' || file.type === 'handbook') articleIds.push(file.id)
              })

              evaluateMove({
                onSelectFolderPayload: data,
                fileMoveAttemptPayload: {
                  folderIds,
                  articleIds,
                  originalWorkspaceId: workspaceId,
                },
                handleMove: moveFiles,
              })

              setVisible(false)
              return
            }
            if (showCopy) {
              setVisible(false)
              copyFiles(data)
              return
            }
          }}
          isRootDisabled={selectedFiles.some((file) => file.type === 'article') || !initialFolder}
          disabledFolderIds={selectedFiles.reduce((acc: string[], file) => {
            if (file.type === 'folder') acc.push(file.id)
            return acc
          }, [])}
        />
      )
    }

    return (
      <NavRightClickMenu
        {...rest}
        setVisible={setVisible}
        selectedFiles={selectedFiles}
        initialFolder={initialFolder}
        onMoveRequest={onMoveRequest}
        onCopyRequest={onCopyRequest}
        onAction={(action) => {
          if (action === 'move') {
            setShowMove(true)
            return
          }
          if (action === 'copy') {
            setShowCopy(true)
            return
          }
          onAction?.(action)
        }}
      />
    )
  },
)
